import React, { Component } from "react";
import Auth from "../routes/Auth";
import RoundNotStartedGate from "../components/round-not-started";
import AddProductPage from "../components/pd/decisions/add-product-page";
import { deleteRequest, patchRequest } from "../api/ApiService";
import {
  submitDecisions,
  incrementUnacceptedDecisions,
  decrementUnacceptedDecisions,
} from "../api/SubmitDecisions";
import { fetchTeamDecisions, fetchTeamStatus } from "../api/TeamDecisionsService";
import LoadingIndicator from "../components/ui-building-blocks/loading/LoadingIndicator";
import SubmitDecisionsButton from "../components/ui-building-blocks/buttons/submit-decisions";
import { logError } from "../services/utils";
import PDSensor from "../components/pd/decisions/PDSensor";
import ImageSensor from "./assets/ImageSensor.png";
import RoleButtons from "../components/role-buttons";
import OpsStyled from "components/operations/style";
import FinanceStyled from "components/finance/style";
import styled from "styled-components";
import { view } from "@risingstack/react-easy-state";
import LastStepModal from '../components/get-started/last-step';
import EventEmitter from "../services/event-emitter";
import SuccessAlert from "../components/alerts/success";
import ErrorAlert from "../components/alerts/error";
import WarningAlert from "../components/alerts/warning";
import FirstDecisionModal from "../components/first-decision-modal";

const PRODUCTS_URL = `${process.env.REACT_APP_API_URL}team/products/`;

const FEATURES = [
  { key: "pixels_rank", durationKey: "pixels_duration", label: "Resolution" },
  { key: "sensitivity_rank", durationKey: "sensitivity_duration", label: "Sensitivity" },
  { key: "power_rank", durationKey: "power_duration", label: "Power" },
  { key: "temp_range_rank", durationKey: "temp_range_duration", label: "Temperature" },
];

const ROUND_1_MAX = 2;
const TOTAL_MAX = 4;

// ---------- styled components (lead-page only) ----------

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E9F0;
  border-radius: 14px;
  padding: 22px 24px;
  box-shadow: 0 1px 2px rgba(15, 23, 41, 0.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const CardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const SensorWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #F5F7FB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductName = styled.div`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: rgba(0, 27, 59, 0.94);
  line-height: 1.2;
  word-break: break-word;
`;

const ProductMeta = styled.div`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 12px;
  color: rgba(0, 27, 59, 0.6);
  font-weight: 600;
  margin-top: 4px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  border-radius: 10px;
  color: rgba(0, 27, 59, 0.6);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${p => (p.danger ? "#DC2626" : "#3A507B")};
    color: ${p => (p.danger ? "#DC2626" : "#3A507B")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RDCost = styled.div`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 10px;

  strong {
    color: #C62828;
    font-weight: 700;
    font-size: 16px;
    margin-left: 4px;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 30px 110px;
  gap: 12px;
  align-items: center;
  font-family: 'Mukta Vaani', Lato, sans-serif;
`;

const FeatureLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.7);
`;

const RankValue = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: rgba(0, 27, 59, 0.94);
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

// Custom div-based slider for product feature ranks (1-10, integer
// steps). Identical to the slider used in the add-product overlay —
// duplicated inline here to keep the card editable in place. Drag
// updates flow back via props.onChange(value).
const SliderRoot = styled.div`
  position: relative;
  height: 22px;
  display: flex;
  align-items: center;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  touch-action: none;
  user-select: none;
  min-width: 0;
`;

const SliderTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 6px;
  background: #E5E9F0;
  border-radius: 999px;
  pointer-events: none;
`;

const SliderFill = styled.div`
  position: absolute;
  left: 0;
  height: 6px;
  background: #A98DF6;
  border-radius: 999px;
  pointer-events: none;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  background: #FFFFFF;
  border: 3px solid #A98DF6;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(169, 141, 246, 0.30);
  transform: translateX(-50%);
  top: 50%;
  margin-top: -9px;
  pointer-events: none;
`;

class FeatureSlider extends Component {
  constructor(props) {
    super(props);
    this.state = { dragging: false };
  }

  componentWillUnmount() {
    this.detach();
  }

  setRef = (el) => { this.rootEl = el; };

  attach() {
    window.addEventListener("mousemove", this.onWindowMove);
    window.addEventListener("mouseup", this.onWindowUp);
    window.addEventListener("touchmove", this.onWindowTouchMove, { passive: false });
    window.addEventListener("touchend", this.onWindowUp);
  }

  detach() {
    window.removeEventListener("mousemove", this.onWindowMove);
    window.removeEventListener("mouseup", this.onWindowUp);
    window.removeEventListener("touchmove", this.onWindowTouchMove);
    window.removeEventListener("touchend", this.onWindowUp);
  }

  updateFromX = (clientX) => {
    if (!this.rootEl || this.props.disabled) return;
    const rect = this.rootEl.getBoundingClientRect();
    if (rect.width <= 0) return;
    const { min, max } = this.props;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let raw = min + (max - min) * ratio;
    raw = Math.round(raw);
    raw = Math.max(min, Math.min(max, raw));
    if (this.props.onChange) this.props.onChange(raw);
  };

  onMouseDown = (e) => {
    if (this.props.disabled) return;
    e.preventDefault();
    this.setState({ dragging: true });
    this.attach();
    this.updateFromX(e.clientX);
  };

  onTouchStart = (e) => {
    if (this.props.disabled || !e.touches || !e.touches.length) return;
    this.setState({ dragging: true });
    this.attach();
    this.updateFromX(e.touches[0].clientX);
  };

  onWindowMove = (e) => {
    if (!this.state.dragging) return;
    this.updateFromX(e.clientX);
  };

  onWindowTouchMove = (e) => {
    if (!this.state.dragging || !e.touches || !e.touches.length) return;
    e.preventDefault();
    this.updateFromX(e.touches[0].clientX);
  };

  onWindowUp = () => {
    if (!this.state.dragging) return;
    this.setState({ dragging: false });
    this.detach();
  };

  render() {
    const { value, min, max, disabled } = this.props;
    const range = max - min;
    const pct = range > 0
      ? Math.max(0, Math.min(100, ((Number(value) - min) / range) * 100))
      : 0;
    return (
      <SliderRoot
        innerRef={this.setRef}
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchStart}
        disabled={disabled}
      >
        <SliderTrack />
        <SliderFill style={{ width: `${pct}%` }} />
        <SliderThumb style={{ left: `${pct}%` }} />
      </SliderRoot>
    );
  }
}

// Pill-shaped Released / Not Released Yet badge mirroring the Manage
// Production page. Hovering "Not Released Yet" surfaces a short tooltip
// explaining the constraint.
const StatusBadgeWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.status-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.3px;
  margin-left: 10px;

  ${p => p.variant === 'released' && `
    background: #E8F5EC;
    color: #2DA548;
    border: 1px solid #B7DEC2;
  `}
  ${p => p.variant === 'not-released' && `
    background: #FFF1DD;
    color: #C77700;
    border: 1px solid #F0CB91;
    cursor: help;
  `}
`;

const StatusTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  padding: 8px 12px;
  border-radius: 6px;
  width: 260px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

// Wrapper that anchors a tooltip above the disabled Add Product button
// in Year 1. Anchors below the button (top: 100%) so it doesn't collide
// with the page title above.
const AddProductTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.add-product-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const AddProductTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 0;
  transform: translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 300px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 24px;
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

// Pencil icon for the per-card edit action. Same chrome as TrashButton
// so the two sit visually as a paired action group on the card header.
const EditButton = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #3A507B;
    color: #3A507B;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Trash icon for the per-card delete action. Inline SVG keeps it free
// from icon-pack dependencies and lets us recolor on hover.
const TrashButton = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #DC2626;
    color: #DC2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Wrapper + tooltip for the disabled trash button on prior-round
// products. Anchored below the icon so the tooltip doesn't collide
// with the card header above.
const TrashTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.trash-disabled-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const TrashDisabledTooltip = styled.span`
  position: absolute;
  top: 100%;
  right: 0;
  transform: translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 280px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 12px;
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

const ColumnHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 30px 110px;
  gap: 12px;
  align-items: center;
  margin-bottom: 4px;
  padding: 0;
`;

const ColumnHeader = styled.div`
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.4);
  text-align: center;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
`;

const UpdateButton = styled.button`
  background: ${p => (p.active ? "#3A507B" : "#E5E9F0")};
  color: ${p => (p.active ? "#FFFFFF" : "rgba(0, 27, 59, 0.4)")};
  border: none;
  padding: 10px 22px;
  border-radius: 10px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: ${p => (p.active ? "pointer" : "not-allowed")};
  transition: background 0.15s ease, transform 0.15s ease;
  box-shadow: ${p => (p.active ? "0 4px 12px rgba(58, 80, 123, 0.25)" : "none")};

  &:hover:not(:disabled) {
    background: #2E3F62;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: ${p => (p.active ? 0.6 : 1)};
    cursor: not-allowed;
    transform: none;
  }
`;

const ReleaseCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.6);
  background: #F5F7FB;
  border-radius: 8px;
  padding: 4px 8px;
  text-align: center;
`;

const EmptyState = styled.div`
  background: #FFFFFF;
  border: 1px dashed #E5E9F0;
  border-radius: 14px;
  padding: 36px 24px;
  text-align: center;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: rgba(0, 27, 59, 0.6);

  h3 {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 800;
    color: rgba(0, 27, 59, 0.94);
  }
  p {
    margin: 0;
    font-size: 14px;
  }
`;

// ---------- component ----------

class PDDecisions extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    const role = Auth.getRole();
    const has_access = role === "CEO" || role === "PD";
    const isOwnRole = role === "PD";

    this.state = {
      products: {},
      product_manufactures: [],
      // team_products is the enriched product list from team-decisions
      // (role=product). We need months_available_to_produce off it to
      // drive the Released / Not Released Yet badge.
      team_products: [],
      // Buffered slider edits keyed by product id. Empty until the user
      // moves a slider; cleared on successful PATCH. Used both to drive
      // the slider UI and to gate the Update button.
      editedRanks: {},
      // Product id currently being PATCHed — disables Update + sliders
      // for just that card.
      savingProductId: null,
      loading: true,
      hasAccess: has_access,
      isOwnRole,
      roleAlert: false,
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      submission_data: {
        pd: { date: "N/A", unaccepted: 0 },
      },
      submitDecisionsLoading: false,
      rounds: props.rounds,
      isPreChecked: false,
      successMessage: null,
      errorMessage: null,
      showAddProductPage: false,
      // First-time coaching popup after the team's first product
      // creation in Round 1. See handleFirstProductCreated below.
      firstProductModalOpen: false,
      deleteConfirmationUid: null,
      // Product currently being edited via the Edit Product popup.
      // null when no edit popup is open; otherwise the uid of the
      // product whose features the user is changing.
      editingUid: null,
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  showSuccessMessage = successMessage => {
    this.setState({ successMessage }, () => this.hideSuccessMessage());
  };

  hideSuccessMessage = () => {
    setTimeout(() => {
      if (!this._isMounted) return;
      this.setState({ successMessage: null });
    }, 5000);
  };

  setErrorMessage = (title, message) => {
    this.setState({ errorMessage: { title, message } });
  };

  hideErrorMessage = () => {
    this.setState({ errorMessage: null });
  };

  reloadRole = () => {
    const role = Auth.getRole();
    this.setState({
      hasAccess: role === "CEO" || role === "PD",
      isOwnRole: role === "PD",
    });
  };

  showRoleAlert = () => this.setState({ roleAlert: true });
  hideRoleAlert = () => this.setState({ roleAlert: false });

  submitDecisions = () => {
    this.setState({ submitDecisionsLoading: true });
    submitDecisions(
      "development",
      this.state.roundInUrl || this.state.currentRound,
      () => this.getSubmissionData()
    );
  };

  loadProducts = () => {
    const { rounds, roundInUrl, currentRound } = this.state;

    if (!rounds || rounds.length === 0) {
      this.timer = setTimeout(this.loadProducts, 1000);
      return;
    }

    const roundNumber = roundInUrl || currentRound;

    // team-decisions (role=product) is the single source of truth for
    // this page. The response already carries the round-scoped
    // products[] (with rnd_costs, ranks, durations, and
    // months_available_to_produce) plus product_manufactures[] — so
    // there's no need for separate /products/ or /product_manufactures/
    // round-trips. team-status still gives us submission_data.pd.
    Promise.all([
      fetchTeamStatus(roundNumber),
      fetchTeamDecisions(roundNumber, 'product'),
    ])
      .then(([statusData, decisionsData]) => {
        if (!this._isMounted) return;

        const currentYear = this.currentYear;
        const team_products = decisionsData.products || [];

        // Render code keys products by uid → year → product. Every row
        // in the team-decisions response is for the requested round, so
        // we always nest under currentYear.
        const productsByUid = {};
        team_products.forEach(product => {
          if (!product.uid) return;
          if (!(product.uid in productsByUid)) productsByUid[product.uid] = {};
          productsByUid[product.uid][currentYear] = product;
        });

        // team-decisions already returns product_manufactures scoped to
        // the requested round, so we can use the array as-is.
        const product_manufactures = decisionsData.product_manufactures || [];

        const isPreChecked = statusData.submission_data.pd
          && statusData.submission_data.pd.date !== 'N/A';

        this.setState({
          products: productsByUid,
          product_manufactures,
          team_products,
          // Wipe any buffered slider edits — the freshly-loaded ranks
          // are the new source of truth.
          editedRanks: {},
          loading: false,
          isPreChecked,
          submission_data: statusData.submission_data,
          submitDecisionsLoading: false,
        });
      })
      .catch(err => {
        logError(err, "PDDecisions", "loadProducts");
        if (this._isMounted) this.setState({ loading: false });
      });
  };

  getSubmissionData = () => {
    this.setState({ submitDecisionsLoading: true });
    this.loadProducts();
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadProducts();
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      this.state.rounds !== nextProps.rounds
    ) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl,
          currentRound: nextProps.currentRound,
          rounds: nextProps.rounds,
        },
        () => {
          this.loadProducts();
          this.reloadRole();
        }
      );
    }
  }

  // ---------- gating + helpers ----------

  get currentYear() {
    const { roundInUrl, currentRound } = this.state;
    if (roundInUrl !== null && roundInUrl !== currentRound) return roundInUrl;
    return currentRound;
  }

  get actionsAllowed() {
    if (this.state.hasAccess === false) return false;
    if (this.props.roundShouldBeFinished === true) return false;
    const { roundInUrl, currentRound, rounds } = this.state;
    if (roundInUrl !== null && roundInUrl !== currentRound) return false;
    const round = rounds.find(i => i.number === currentRound);
    if (!round) return false;
    return round.is_started === true && round.is_finished === false;
  }

  get isRound1() {
    return Number(this.currentYear) === 1;
  }

  get productCount() {
    return Object.keys(this.state.products).length;
  }

  get maxProductsForRound() {
    return this.isRound1 ? ROUND_1_MAX : TOTAL_MAX;
  }

  get canAddProduct() {
    return this.actionsAllowed && this.productCount < this.maxProductsForRound;
  }

  // ---------- KPI computations ----------

  get totalRDCosts() {
    const { products } = this.state;
    const currentYear = this.currentYear;
    return Object.keys(products).reduce((sum, uid) => {
      const data = products[uid][currentYear] || {};
      return sum + Math.abs(Number(data.rnd_costs) || 0);
    }, 0);
  }

  // Mirrors the Manage Production page: a product is "Released" when
  // months_available_to_produce > 0 on the team-decisions products
  // entry for this product id.
  isProductReleased = (productId) => {
    const tp = (this.state.team_products || []).find(
      p => Number(p.id) === Number(productId)
    );
    if (!tp) return false;
    return (Number(tp.months_available_to_produce) || 0) > 0;
  };

  // ---------- handlers ----------

  // Fired by AddProductPage after the team's first successful product
  // creation in Round 1. Shows a coaching modal reminding students they
  // can edit the product before deadline, add a second one, and must
  // ensure it releases in the current round or the team scores 0%.
  handleFirstProductCreated = () => {
    this.setState({ firstProductModalOpen: true });
  };

  handleFirstProductClose = () => {
    this.setState({ firstProductModalOpen: false });
  };

  openAddProductPage = () => {
    if (!this.state.hasAccess) {
      this.showRoleAlert();
      return;
    }
    if (!this.canAddProduct) {
      this.setErrorMessage(
        "Can't add another product",
        this.isRound1
          ? `You can create up to ${ROUND_1_MAX} products in Year 1. Submit your existing products first, then add more in later years.`
          : `You already have the maximum of ${TOTAL_MAX} products. Edit an existing product to refine it.`
      );
      return;
    }
    this.setState({ showAddProductPage: true });
  };

  closeAddProductPage = () => this.setState({ showAddProductPage: false });

  // Get the current rank value for a product/feature, either from
  // buffered local edits or (falling back to) the saved product row.
  rankFor = (productData, key) => {
    const edits = this.state.editedRanks[productData.id];
    if (edits && edits[key] != null) return Number(edits[key]);
    return Number(productData[key]) || 0;
  };

  // Has the user moved any slider on this product? Drives the Update
  // button's active state.
  isDirtyFor = (productData) => {
    const edits = this.state.editedRanks[productData.id];
    if (!edits) return false;
    return FEATURES.some(feature => {
      const next = Number(edits[feature.key]);
      const prev = Number(productData[feature.key]) || 0;
      return Number.isFinite(next) && next !== prev;
    });
  };

  onRankChange = (productData, key) => (value) => {
    if (!this.actionsAllowed) return;
    const existing = this.state.editedRanks[productData.id] || {};
    this.setState({
      editedRanks: {
        ...this.state.editedRanks,
        [productData.id]: {
          ...existing,
          [key]: value,
        },
      },
    });
  };

  updateProduct = (productData) => () => {
    if (!this.actionsAllowed) return;
    if (!this.isDirtyFor(productData)) return;
    if (!this.state.hasAccess) {
      this.showRoleAlert();
      return;
    }

    // Block updates if Operations has already started production —
    // mirrors the legacy guard so the request doesn't succeed and
    // leave orphaned manufacture rows behind. A manufacture row with
    // units_produced === 0 is a draft / cleared decision and does not
    // count as "in production".
    const inProduction = this.state.product_manufactures.some(
      m => Number(m.product) === Number(productData.id)
        && Number(m.units_produced) > 0
    );
    if (inProduction) {
      this.setErrorMessage(
        "Product is in production",
        "You can't edit a product's features while it's in production. Cancel the production decision in Operations first, then come back and edit."
      );
      return;
    }

    const edits = this.state.editedRanks[productData.id] || {};
    const payload = {};
    FEATURES.forEach(feature => {
      payload[feature.key] = edits[feature.key] != null
        ? edits[feature.key]
        : Number(productData[feature.key]) || 0;
    });

    this.setState({ savingProductId: productData.id });

    patchRequest(`${PRODUCTS_URL}${productData.id}/`).send(payload)
      .then(() => {
        incrementUnacceptedDecisions("development");
        if (!this._isMounted) return;
        this.setState({ savingProductId: null });
        this.showSuccessMessage(`Product "${productData.name}" was successfully updated.`);
        this.loadProducts();
      })
      .catch(err => {
        logError(err, "PDDecisions", "updateProduct");
        if (this._isMounted) {
          this.setState({
            savingProductId: null,
            errorMessage: {
              title: "Couldn't update product",
              message: "Something went wrong saving these changes. Please try again.",
            },
          });
        }
      });
  };

  // Edit is the only path for changing a product's feature ranks now —
  // the inline sliders are read-only. Mirrors the legacy in-production
  // guard before opening the popup so the user sees the explanation
  // before they start changing values.
  requestEditProduct = (uid) => () => {
    if (!this.state.hasAccess) {
      this.showRoleAlert();
      return;
    }
    const productRounds = this.state.products[uid];
    const productData = productRounds ? productRounds[this.currentYear] : null;
    if (!productData) return;

    const inProduction = this.state.product_manufactures.some(
      m => Number(m.product) === Number(productData.id)
        && Number(m.units_produced) > 0
    );
    if (inProduction) {
      this.setErrorMessage(
        "Product is in production",
        "You can't edit a product's features while it's in production. Cancel the production decision in Operations first, then come back and edit."
      );
      return;
    }

    this.setState({ editingUid: uid });
  };

  closeEditProductPage = () => {
    this.setState({ editingUid: null });
  };

  // Delete is only allowed for a product that was CREATED in the round
  // currently being viewed. A product carried over from an earlier round
  // surfaces the friendly message: once it exists in a prior round it
  // can only be upgraded or abandoned, not removed.
  //
  // Detection: each product row carries `origin_round` (the id of the
  // round it was first created in). If that doesn't match the current
  // round's id, it's a carry-over.
  requestDeleteProduct = (uid) => () => {
    if (!this.state.hasAccess) {
      this.showRoleAlert();
      return;
    }

    const { products, rounds } = this.state;
    const productData = products[uid] && products[uid][this.currentYear];
    const currentRoundObj = (rounds || []).find(
      r => Number(r.number) === Number(this.currentYear)
    );
    const currentRoundId = currentRoundObj ? Number(currentRoundObj.id) : null;
    const createdThisRound = productData
      && currentRoundId != null
      && Number(productData.origin_round) === currentRoundId;

    if (!createdThisRound) {
      this.setErrorMessage(
        "Product deletion is only available in the round it was created",
        "Once a product carries over into a later round, you can either keep upgrading it to make it more competitive in the market, or stop investing in it and put resources into your other products. Editing the product is the way to make those upgrades."
      );
      return;
    }
    this.setState({ deleteConfirmationUid: uid });
  };

  cancelDeleteProduct = () => this.setState({ deleteConfirmationUid: null });

  confirmDeleteProduct = () => {
    const uid = this.state.deleteConfirmationUid;
    if (!uid) return;
    const productRounds = this.state.products[uid];
    const productData = productRounds ? productRounds[this.currentYear] : null;
    if (!productData) {
      this.setState({ deleteConfirmationUid: null });
      return;
    }

    // Block deletion if Operations has already started production for
    // this product — mirrors the legacy guard so the request doesn't
    // succeed and then leave orphaned manufacture rows behind. A
    // manufacture row with units_produced === 0 is a draft / cleared
    // decision and does not count as "in production".
    const inProduction = this.state.product_manufactures.some(
      m => Number(m.product) === Number(productData.id)
        && Number(m.units_produced) > 0
    );
    if (inProduction) {
      this.setState({
        deleteConfirmationUid: null,
        errorMessage: {
          title: "You cannot delete this product yet",
          message: "Operations has already started production for this product. Cancel that production decision first, then delete.",
        },
      });
      return;
    }

    deleteRequest(`${PRODUCTS_URL}${productData.id}/`)
      .then(() => {
        decrementUnacceptedDecisions("development");
        this.setState({ deleteConfirmationUid: null });
        this.showSuccessMessage(`Product "${productData.name}" was successfully deleted.`);
        this.loadProducts();
      })
      .catch(err => {
        logError(err, "PDDecisions", "confirmDeleteProduct");
        this.setState({
          deleteConfirmationUid: null,
          errorMessage: {
            title: "Couldn't delete product",
            message: "Something went wrong deleting this product. Please try again.",
          },
        });
      });
  };

  // ---------- render ----------


  renderProductCard = (uid) => {
    const { products, rounds } = this.state;
    const productRounds = products[uid];
    const currentYear = this.currentYear;
    const productData = productRounds[currentYear];
    if (!productData) return null;

    const rndCosts = Math.abs(Number(productData.rnd_costs) || 0);
    const fmtMoney = v => `$${Math.round(v).toLocaleString('en-US')}`;
    const released = this.isProductReleased(productData.id);
    // Sliders are always read-only — feature changes go through the
    // Edit Product popup. Saving spinner stays scoped to that flow.
    const slidersDisabled = true;

    // Prior-round products can't be deleted. BE surfaces `origin_round`
    // on each product row: if it doesn't match the current round's id,
    // the product was created earlier and we disable the trash icon +
    // explain why on hover. Same rule as requestDeleteProduct, but
    // pre-checked so a stray click never opens the error popup.
    const currentRoundObj = (rounds || []).find(
      r => Number(r.number) === Number(this.currentYear)
    );
    const currentRoundId = currentRoundObj ? Number(currentRoundObj.id) : null;
    const priorRoundProduct = currentRoundId != null
      && Number(productData.origin_round) !== currentRoundId;

    return (
      <ProductCard key={uid}>
        <CardTopRow>
          <CardLeft>
            <SensorWrap>
              <img src={ImageSensor} alt="" />
            </SensorWrap>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <ProductName>{productData.name}</ProductName>
                {released ? (
                  <StatusBadge variant="released">Released</StatusBadge>
                ) : (
                  <StatusBadgeWrapper>
                    <StatusBadge variant="not-released">Not Released Yet</StatusBadge>
                    <StatusTooltip className="status-tooltip">
                      This product won't be released this year because one or
                      more features have a release date beyond the current year.
                      Check the Release Date column to see which features are
                      pushing the timeline out.
                    </StatusTooltip>
                  </StatusBadgeWrapper>
                )}
              </div>
              <RDCost>R&amp;D Costs <strong>{fmtMoney(rndCosts)}</strong></RDCost>
            </div>
          </CardLeft>
          {this.actionsAllowed && (
            <CardActions>
              <EditButton
                onClick={this.requestEditProduct(uid)}
                aria-label={`Edit ${productData.name}`}
                title={`Edit ${productData.name}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </EditButton>
              {priorRoundProduct ? (
                <TrashTooltipWrapper>
                  <TrashButton
                    disabled
                    aria-label={`Delete ${productData.name}`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                    </svg>
                  </TrashButton>
                  <TrashDisabledTooltip className="trash-disabled-tooltip">
                    You can't delete a product that was created in a prior
                    round. Modifying it is more cost effective than building
                    a new one.
                  </TrashDisabledTooltip>
                </TrashTooltipWrapper>
              ) : (
                <TrashButton
                  onClick={this.requestDeleteProduct(uid)}
                  aria-label={`Delete ${productData.name}`}
                  title={`Delete ${productData.name}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                  </svg>
                </TrashButton>
              )}
            </CardActions>
          )}
        </CardTopRow>

        <ColumnHeaderRow>
          <ColumnHeader style={{ textAlign: 'left' }}>Feature</ColumnHeader>
          <ColumnHeader>&nbsp;</ColumnHeader>
          <ColumnHeader>Quality Level</ColumnHeader>
          <ColumnHeader>Release<br />Date</ColumnHeader>
        </ColumnHeaderRow>

        <FeatureList>
          {FEATURES.map(feature => {
            const rank = this.rankFor(productData, feature.key);
            return (
              <FeatureRow key={feature.key}>
                <FeatureLabel>{feature.label}</FeatureLabel>
                <FeatureSlider
                  min={1}
                  max={10}
                  value={rank}
                  onChange={this.onRankChange(productData, feature.key)}
                  disabled={slidersDisabled}
                />
                <RankValue>{rank}</RankValue>
                <ReleaseCell>
                  <PDSensor
                    product={productRounds}
                    currentRound={currentYear}
                    rounds={rounds}
                    showReleaseDate={true}
                    showImage={false}
                    showAge={false}
                    duration_field={feature.durationKey}
                  />
                </ReleaseCell>
              </FeatureRow>
            );
          })}
        </FeatureList>

      </ProductCard>
    );
  };

  renderProductsSection() {
    const { products } = this.state;
    const currentYear = this.currentYear;
    // Newest product first so the latest creation sits in the top-left
    // slot of the grid, then fills rightward. Product ids are
    // auto-incremented so higher id = more recently created.
    const uids = Object.keys(products).sort((a, b) => {
      const aId = Number((products[a] && products[a][currentYear] || {}).id) || 0;
      const bId = Number((products[b] && products[b][currentYear] || {}).id) || 0;
      return bId - aId;
    });

    if (uids.length === 0) {
      return null;
    }

    return (
      <ProductsGrid>
        {uids.map(this.renderProductCard)}
      </ProductsGrid>
    );
  }

  render() {
    // Lock the page for students only when no round has ever been
    // started. Does not fire for finished games (see ceo-decisions.js
    // for the full explanation).
    const { rounds, rounds_loaded } = this.props;
    const hasEverStarted = (rounds || []).some(
      r => r && r.is_started === true
    );
    if (rounds_loaded && !hasEverStarted) {
      return <RoundNotStartedGate />;
    }

    const {
      location,
      router,
      currentRound: round,
    } = this.props;
    const {
      products,
      isOwnRole,
      roleAlert,
      submission_data,
      submitDecisionsLoading,
      loading,
      isPreChecked,
      successMessage,
      errorMessage,
      showAddProductPage,
      deleteConfirmationUid,
    } = this.state;
    const actionsAllowed = this.actionsAllowed;
    const canAddProduct = this.canAddProduct;
    const showAddProductButton = actionsAllowed && !loading;
    const deleteTarget = deleteConfirmationUid && products[deleteConfirmationUid]
      ? products[deleteConfirmationUid][this.currentYear]
      : null;

    return (
      <OpsStyled.BodyWrapper>
        <LastStepModal
          location={location}
          router={router}
          url={`/decisions/product-development?round=${round}`}
        />
        <OpsStyled.TitleWrapper>
          <OpsStyled.Title id="product-development-decisions-title">
            Product Management
            {isOwnRole && <RoleButtons type="product_development" />}
          </OpsStyled.Title>
          {actionsAllowed === true && loading === false && (
            <SubmitDecisionsButton
              id="product-development-submit-decisions"
              tooltipPlacement="bottom"
              data={submission_data.pd}
              isLoading={submitDecisionsLoading}
              onClick={this.submitDecisions}
              buttonComponent={OpsStyled.SubmitDecisionButton}
              isPreChecked={isPreChecked}
            />
          )}
        </OpsStyled.TitleWrapper>
        <OpsStyled.Subtitle>
          Design an image sensor product to sell. Every product is built on four features: Resolution, Sensitivity, Power, and Temperature. You can create up to 2 in Year 1, plus 2 more in later years. Keep upgrading existing products each year to stay competitive.
        </OpsStyled.Subtitle>

        {showAddProductButton && (
          <FinanceStyled.ManageButtonBar style={{ marginTop: 20 }}>
            <AddProductTooltipWrapper
              style={!canAddProduct ? { cursor: 'not-allowed' } : undefined}
            >
              <FinanceStyled.ManageButton
                onClick={this.openAddProductPage}
                disabled={!canAddProduct}
                style={!canAddProduct ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
              >
                Add Product
              </FinanceStyled.ManageButton>
              {!canAddProduct && this.isRound1 && (
                <AddProductTooltip className="add-product-tooltip">
                  You can only create {ROUND_1_MAX} products in Year 1. You'll be able to add more in later years.
                </AddProductTooltip>
              )}
            </AddProductTooltipWrapper>
          </FinanceStyled.ManageButtonBar>
        )}

        {roleAlert && (
          <ErrorAlert
            title="Error | Not Authorized"
            message="You are not authorized to make decisions for this role."
            onHide={this.hideRoleAlert}
          />
        )}
        {errorMessage && (
          <ErrorAlert
            title={errorMessage.title}
            message={errorMessage.message}
            onHide={this.hideErrorMessage}
          />
        )}
        {successMessage && <SuccessAlert message={successMessage} />}

        <LoadingIndicator loading={loading} />

        {loading === false && (
          <FinanceStyled.KpiRow cardCount={4} style={{ marginTop: 16 }}>
            <FinanceStyled.KpiCard>
              <FinanceStyled.KpiLabel>Total R&amp;D Costs</FinanceStyled.KpiLabel>
              <FinanceStyled.KpiValue style={{ color: '#C62828' }}>
                ${Math.round(this.totalRDCosts).toLocaleString('en-US')}
              </FinanceStyled.KpiValue>
            </FinanceStyled.KpiCard>
          </FinanceStyled.KpiRow>
        )}

        {loading === false && (
          <FinanceStyled.SectionCard style={{ marginTop: 16 }}>
            <FinanceStyled.SectionHeader>
              <FinanceStyled.SectionTitle>Products</FinanceStyled.SectionTitle>
              <FinanceStyled.SectionSubtitle>
                Create a product to sell to customers.
              </FinanceStyled.SectionSubtitle>
            </FinanceStyled.SectionHeader>
            {this.renderProductsSection()}
          </FinanceStyled.SectionCard>
        )}

        {showAddProductPage && (
          <AddProductPage
            existingProducts={products}
            reloadProducts={this.loadProducts}
            showSuccessMessage={this.showSuccessMessage}
            onClose={this.closeAddProductPage}
            currentRound={this.state.currentRound}
            onFirstProductCreated={this.handleFirstProductCreated}
          />
        )}

        <FirstDecisionModal
          open={this.state.firstProductModalOpen}
          title="Nice work on creating your first product!"
          body="You can edit this product any time before the round deadline. You can also add a second product. Make sure your product is released in the current round, otherwise your team will get a 0% score for this round."
          ctaLabel="Got it"
          onCta={this.handleFirstProductClose}
          onClose={this.handleFirstProductClose}
        />


        {this.state.editingUid && products[this.state.editingUid]
          && products[this.state.editingUid][this.currentYear] && (
          <AddProductPage
            editingProduct={products[this.state.editingUid][this.currentYear]}
            existingProducts={products}
            reloadProducts={this.loadProducts}
            showSuccessMessage={this.showSuccessMessage}
            onClose={this.closeEditProductPage}
          />
        )}

        {deleteTarget && (
          <WarningAlert
            title="Delete Product"
            successButtonText="Yes, Delete"
            onSuccess={this.confirmDeleteProduct}
            onCancel={this.cancelDeleteProduct}
            message={(
              <span>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                This can't be undone, and it will remove any pricing or promo
                decisions tied to this product.
              </span>
            )}
          />
        )}
      </OpsStyled.BodyWrapper>
    );
  }
}

export default view(PDDecisions);



// WEBPACK FOOTER //
// ./src/containers/PDDecisions.js